<template>
  <modal
    v-if="!loading"
    class="style-modal"
    @close-modal="close"
  >
    <create-offer-modal
      :full-game="game"
      :session="updatedSession"
      :bid-ladder="bidLadder"
      :ask-ladder="askLadder"
      :spread-number="Number(updatedSession.spread)"
      :total-number="Number(updatedSession.total)"
      :is-new-offer="true"
      @close-modal="close"
      @spread-update="spreadUpdate"
      @side-update="sideUpdate"
      @offer-placed="phOutcome = 'offer_placed'"
    />
  </modal>
  <modal v-else class="modal-wrap" @close-modal="close">
    <spinner />
  </modal>
</template>

<script setup>
import Modal from '@/components/global/Modal.vue';
import Spinner from '@/components/global/Spinner.vue';
import { ref, computed, toRaw, onMounted, onBeforeUnmount } from 'vue';
import { useUserStore } from '@/stores/user';
import { PostHogUtils, resolveEmptyBalance } from '@/core/PostHogUtils';
import {
  calculateWin,
  calculateRisk,
  getAnchoredOfferSide,
  getOtherSideType,
  convertAmericanToDecimal,
  getMarketSubtype,
} from '@/core/utils';
import { getAmericanTickInfo } from '@/core/ticks';
import { formatBidAndAsk } from '@/core/modalUtils';
import CreateOfferModal from './CreateOfferModal.vue';

const props = defineProps({
  game: {
    type: Object,
    required: true,
  },
  session: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['closeModal']);
const userStore = useUserStore();
const updatedSpread = ref(0);
const updatedSide = ref(null);
const updatedSession = ref({});
/**
 * Functionality for helpful computed values (Take Bet) (Create Offer)
 */
const betType = ref(props.session.type);
const sideType = computed(() => {
  if (props.session?.type === 'total') {
    return props.session.OU;
  } else if (
    props.session?.type === 'spread' ||
    props.session?.type === 'moneyline'
  ) {
    const participantI = props.game.participants.findIndex(
      (el) => el.id === props.session.participantID,
    );
    const home = props.game.participants[participantI].homeAway === 'home';
    if (props.session?.type === 'spread') {
      return home ? 'homeSpreads' : 'awaySpreads';
    }
    return home ? 'homeMoneylines' : 'awayMoneylines';
  } else if (props.session?.type === 'moneyline1x2') {
    if (props.session.market !== 'draw') {
      const participantI = props.game.participants.findIndex(
        (el) => el.id === props.session.market,
      );
      const home = props.game.participants[participantI].homeAway === 'home';

      if (home) {
        return { homeAwayDraw: 'homeMoneylines1x2', side: props.session.side };
      } else {
        return { homeAwayDraw: 'awayMoneylines1x2', side: props.session.side };
      }
    } else {
      return { homeAwayDraw: 'draw1x2', side: props.session.side };
    }
  }
});

const side = computed(() => {
  return {
    type: props.session.type,
    spread: props.session.spread,
    total: props.session.total,
    OU: props.session.OU,
    participantID: props.session.participantID,
  };
});

const sendMessage = ref(userStore.user.defaultSendOrderMessage);
function handlePressedUpdate(value) {
  sendMessage.value = value;
}

/***
 * Functionality for changing Risk / Win (Create Offer)
 */
const offerRisk = ref('');
const offerWin = ref('');

/**
 * Functionality to handle the side
 */
const sideSelected = ref();
const sideOptions_sm = props.game.participants
  ? ref([
      {
        name: props.game.participants[0].shortName,
        id: '1',
        participantID: props.game.participants[0].id,
      },
      {
        name: props.game.participants[1].shortName,
        id: '2',
        participantID: props.game.participants[1].id,
      },
    ])
  : null;
const sideOptions_t = ref([
  { name: 'Over', id: '1' },
  { name: 'Under', id: '2' },
]);

const sideOptions_1x2 = ref([
  { name: 'Yes', id: '0' },
  { name: 'No', id: '1' },
]);

function initializeSideSelected() {
  if (props.session.type === 'spread' || props.session.type === 'moneyline') {
    sideSelected.value = sideOptions_sm.value.find(
      (el) => el.participantID === props.session.participantID,
    );
  } else if (props.session.type === 'moneyline1x2') {
    sideSelected.value =
      props.session.side === 'yes'
        ? sideOptions_1x2.value[0]
        : sideOptions_1x2.value[1];
  } else {
    sideSelected.value =
      props.session.OU === 'over'
        ? sideOptions_t.value[0]
        : sideOptions_t.value[1];
  }
}

/**
 * Functionality for changing spread and total number
 */
const spread = ref(0);
const total = ref(0);

function initializeSpreadAndTotal() {
  if (sideType.value === 'awaySpreads') {
    spread.value = props.session.spread ? props.session.spread : 0;
  } else if (sideType.value === 'homeSpreads') {
    spread.value = props.session.spread ? props.session.spread : 0;
  } else if (betType.value === 'total') {
    total.value = props.session.total ? props.session.total : 0;
  }
}

/***
 *  Functionality for other side best session (Create Offer)
 */
const otherSideBestSession = ref({});

function initilizeOtherSideBestSession() {
  const otherSideType = getOtherSideType(sideType.value);
  if (props.session.type === 'moneyline') {
    if (props.game[otherSideType].length > 0) {
      otherSideBestSession.value = JSON.parse(
        JSON.stringify(props.game[otherSideType][0]),
      );
    } else {
      return null;
    }
  } else if (props.session.type === 'spread') {
    if (props.game[otherSideType][-spread.value]?.length > 0) {
      otherSideBestSession.value = JSON.parse(
        JSON.stringify(props.game[otherSideType][-spread.value][0]),
      );
    } else {
      return null;
    }
  }
}

/***
 * Functionality related to the odds (Create Offer)
 */
const offerOdds = ref(100);

function initializeOfferOdds() {
  offerOdds.value = otherSideBestSession.value.odds
    ? -(getAmericanTickInfo(otherSideBestSession.value.odds).next)
    : setOddsWhenNoOtherSide(props.game[props.session.type]);
  if (
    otherSideBestSession.value.odds &&
    convertAmericanToDecimal(-offerOdds.value) >=
      convertAmericanToDecimal(otherSideBestSession.value.odds)
  ) {
    offerOdds.value = -otherSideBestSession.value.odds;
  }
  if (offerOdds.value === props.session.odds) offerOdds.value = getAmericanTickInfo(offerOdds.value).next;
  if (offerOdds.value < 100 && offerOdds.value > -101) offerOdds.value = 100;
  const snapped = getAmericanTickInfo(offerOdds.value).snap;
  const ourBest = props.session?.odds;
  const theirBest = otherSideBestSession.value?.odds;
  // Market width between two opposite-side taker odds is |ourBest + theirBest|.
  // Flipping one side brings both to the same perspective, so summing gives the
  // signed gap. Using |abs(a) - abs(b)| ignores sign and misidentifies tightness
  // (e.g. would treat -104/-103 as "1 tick" when the real gap is far wider).
  const spread = ourBest && theirBest ? Math.abs(ourBest + theirBest) : null;
  const inHalfTickRange = Math.abs(snapped) < 200;
  // Preserve the half-tick whenever the market is already tight (<= 1 wide).
  // Using `=== 1` dropped through to Math.floor for half-tick spreads, which
  // pulls negative half-ticks the wrong way (-105.5 -> -106) and reverts the
  // suggested offer back to the taker odds the user just clicked.
  offerOdds.value = spread !== null && spread <= 1 && inHalfTickRange ? snapped : Math.floor(snapped);
  if (offerOdds.value === -100) offerOdds.value = 100;
}

function setOddsWhenNoOtherSide(orderArr) {
  if (!Array.isArray(orderArr)) {
    return 100;
  }
  const filteredOrder = orderArr.find((item) => {
    if (item && item.participantID === props.session.participantID) {
      return true;
    }
    return false;
  });
  if (filteredOrder) {
    if (filteredOrder.odds === -101) return 100;
    if (filteredOrder.odds > -101 && filteredOrder.odds < -100) return 100;
    return getAmericanTickInfo(filteredOrder.odds).next;
  }
  return 100;
}

// Re-anchor risk/win per the user's create-offer unit mode — same rule as
// CreateOfferModal.handleOddsUpdate; this modal has no ladder sweep flow.
// Note: with the decimal-format first-update workaround below, the first real
// change sees prevOdds from initialization rather than a user edit — safe,
// because amounts start empty here and the !amount branch returns first.
function handleOddsUpdate(newOdds, prevOdds = newOdds) {
  const mode = userStore.user.createOfferUnitMode || 'riskAUnit';
  const anchor = getAnchoredOfferSide(mode, newOdds);
  const carryFrom = getAnchoredOfferSide(mode, prevOdds);
  const amount = parseFloat(
    carryFrom === 'win' ? offerWin.value : offerRisk.value,
  );
  if (!amount) {
    offerWin.value = '';
    return;
  }
  if (anchor === 'win') {
    offerWin.value = amount.toFixed(2);
    offerRisk.value = calculateRisk(amount, newOdds).toFixed(2);
  } else {
    offerRisk.value = amount.toFixed(2);
    offerWin.value = calculateWin(amount, newOdds).toFixed(2);
  }
}

const ignoredInitialUpdate = ref(false);
function updateOdds(value) {
  if (userStore.user.oddsFormat === 'decimal' && !ignoredInitialUpdate.value) {
    ignoredInitialUpdate.value = true;
    return;
  } // the odds selector emits an initial odds update on load that is in the wrong (decimal) format. This is a workaround for it
  const prevOdds = offerOdds.value;
  offerOdds.value = value;
  handleOddsUpdate(value, prevOdds);
}

/***
 * Functionality related to offer expiry value
 */
const expiry = ref('');

function initializeExpiry() {
  if (userStore.user.defaultExpiry) {
    expiry.value = userStore.user.defaultExpiry;
  }
  if (props.game.live) {
    expiry.value = '5';
  }
}

/**
 * Functionality for HTML and CSS
 */
const loading = ref(false);
/**
 * Functionality to set up new offer modal
 */

function setupNewOfferModal() {
  initializeUpdatedSession();
  initializeSideSelected();
  initializeSpreadAndTotal();
  initilizeOtherSideBestSession();
  initializeOfferOdds();
  initializeExpiry();
  initializeSpread();
  initializeSide();
}

function close() {
  emit('closeModal');
}

// This modal is always the "make your own offer" path (no order to take), so it
// mirrors TakeBetModal's open/close tracking with opened_mode fixed to 'offer'.
// outcome flips to 'offer_placed' when CreateOfferModal confirms a post; otherwise
// the user closed the ticket without posting.
const phIsLive = computed(() => !!props.game?.live);
const phMarketType = computed(() =>
  props.game?.league?.includes('PROPS') ? 'prop' : props.session?.type,
);
// Same shared helper as TakeBetModal/CreateOfferModal — every
// bet_modal_opened/closed carries market_subtype from one code path.
// getMarketSubtype falls back to 'standard' on a missing game, never null.
const phMarketSubtype = computed(() =>
  getMarketSubtype(props.game, props.session?.type),
);
const phOutcome = ref('abandoned');

onMounted(() => {
  // "Wants to bet but has no funds" signal — see TakeBetModal for rationale.
  // null when not logged in / balance unknown, so we omit rather than mislead.
  const emptyBalance = resolveEmptyBalance(userStore.user);
  PostHogUtils.capture('bet_modal_opened', {
    is_live: phIsLive.value,
    market_type: phMarketType.value,
    market_subtype: phMarketSubtype.value,
    opened_mode: 'offer',
    ...(emptyBalance !== null && { empty_balance: emptyBalance }),
  });
});

onBeforeUnmount(() => {
  PostHogUtils.capture('bet_modal_closed', {
    outcome: phOutcome.value,
    final_mode: 'offer',
    mode_switch_count: 0,
    is_live: phIsLive.value,
    market_type: phMarketType.value,
    market_subtype: phMarketSubtype.value,
  });
});

// Initialize bidLadder with selected side and spread.
const bidLadder = computed(() => {
  const sideKey = updatedSide.value?.side === 'yes' ? 'Yes' : 'No'; // Add optional chaining for safety
  let sourcePath;

  // Helper function for deep cloning
  const deepClone = (obj) => {
    if (!obj) return obj;
    return structuredClone(toRaw(obj));
  };

  switch (side.value.type) {
    case 'moneyline':
      if (props.game[updatedSide.value]) {
        sourcePath = props.game[updatedSide.value];
      }
      break;
    case 'spread':
      if (props.game[updatedSide.value]?.[updatedSpread.value]) {
        sourcePath = props.game[updatedSide.value][updatedSpread.value];
      }
      break;
    case 'total':
      if (props.game[updatedSide.value]?.[updatedSpread.value]) {
        sourcePath = props.game[updatedSide.value][updatedSpread.value];
      }
      break;
    case 'moneyline1x2':
      if (props.session.market === 'draw') {
        sourcePath = props.game['draw1x2']?.[`draw${sideKey}`];
      } else {
        const homeAwayKey = updatedSide.value?.homeAwayDraw;
        sourcePath =
          props.game[homeAwayKey]?.[
            `${homeAwayKey?.replace('Moneylines1x2', '')}${sideKey}`
          ];
      }
      break;
    default:
      sourcePath = null; // Handle unknown types gracefully
  }

  const bids = deepClone(sourcePath); // Clone only if sourcePath is defined
  return formatBidAndAsk(
    bids,
    props.game.league?.toLowerCase() ?? '',
    props.session.type,
  );
});

// Initialize askLadder with selected side and spread.
const askLadder = computed(() => {
  const sideKey = updatedSide.value?.side === 'yes' ? 'No' : 'Yes'; // Add optional chaining for safety
  const otherSide = getOtherSideType(updatedSide.value);
  let sourcePath;

  // Helper function for deep cloning
  const deepClone = (obj) => {
    if (!obj) return obj;
    return structuredClone(toRaw(obj));
  };

  switch (side.value.type) {
    case 'moneyline':
      if (props.game[otherSide]) {
        sourcePath = props.game[otherSide];
      }
      break;
    case 'spread':
      if (props.game[otherSide]?.[-updatedSpread.value]) {
        sourcePath = props.game[otherSide][-updatedSpread.value];
      }
      break;
    case 'total':
      if (props.game[otherSide]?.[updatedSpread.value]) {
        sourcePath = props.game[otherSide][updatedSpread.value];
      }
      break;
    case 'moneyline1x2':
      if (props.session.market === 'draw') {
        sourcePath = props.game['draw1x2']?.[`draw${sideKey}`];
      } else {
        const homeAwayKey = updatedSide.value?.homeAwayDraw;
        sourcePath =
          props.game[homeAwayKey]?.[
            `${homeAwayKey?.replace('Moneylines1x2', '')}${sideKey}`
          ];
      }
      break;
    default:
      sourcePath = null; // Handle unknown types gracefully
  }

  const asks = deepClone(sourcePath); // Clone only if sourcePath is defined
  return formatBidAndAsk(
    asks,
    props.game.league?.toLowerCase() ?? '',
    props.session.type,
  );
});

// Receives spread value when changed to updated ladder and updates the updatedSession object.
function spreadUpdate(object) {
  updatedSession.value = object.updatedSession;
  updatedSpread.value = object.updatedSpread;
}

// Receives selectedSide value when changed to updated ladder and updates the updatedSession object.
function sideUpdate(object) {
  updatedSession.value = object.updatedSession;

  switch (side.value.type) {
    case 'spread':
      updatedSide.value =
        object.updatedSide.homeAway === 'home' ? 'homeSpreads' : 'awaySpreads';
      break;
    case 'total':
      updatedSide.value =
        object.updatedSide.longName === 'Over' ? 'over' : 'under';
      break;
    case 'moneyline':
      updatedSide.value =
        object.updatedSide.homeAway === 'home'
          ? 'homeMoneylines'
          : 'awayMoneylines';
      break;
    case 'moneyline1x2':
      updatedSide.value.homeAwayDraw = sideType.value.homeAwayDraw;
      updatedSide.value.side = object.updatedSide.longName
        .toLowerCase()
        .includes('yes')
        ? 'yes'
        : 'no';
      break;
    default:
      // Handle unexpected type if needed
      break;
  }
}

// Initial spread initialization.
function initializeSpread() {
  updatedSpread.value =
    side.value.type === 'spread' ? spread.value : total.value;
}

// Initial side initialization.
function initializeSide() {
  updatedSide.value = sideType.value;
}

// Initialize a copy of the initial session value.
function initializeUpdatedSession() {
  updatedSession.value = { ...props.session };
  if (
    props.game.sport === 'custom' &&
    updatedSession.value.type === 'spread' &&
    !updatedSession.value.spread
  ) {
    updatedSession.value.spread = 0;
  }
}

setupNewOfferModal();
</script>

<style scoped lang="scss">
@use '@/scss/main.scss';
</style>
